import React, { Key, useEffect, useMemo, useState } from "react"
import { Flex, Space } from "antd"
import ExpandBtn from "../../../ui/ExpandBtn"
import SponsoredBrandsProjectGroups from "./SponsoredBrandsProjectGroups"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { useActions } from "../../../hooks/useActions"
import { FILTER_TYPES } from "../types/sbTargetApi.types"
import CampaignActions from "./CampaignActions"
import { ICampaignResult, SB_CAMPAIGNS_STATE_FILTER } from "../types/sbCampaignApi.types"
import SbStateFilter from "./SbStateFilter"
import ApplyOnAmazon from "./ApplyOnAmazon"
import ManageFilter from "./ManageFilter"
import KpiSummaryCards from "../../../components/KpiSummaryCards"
import { campaignStates } from "../../../types/campaigns"

const SponsoredBrandsContainer: React.FC = () => {
  const [isExpandedAll, setIsExpandedAll] = useState(false)
  const [stateFilter, setStateFilter] = useState(SB_CAMPAIGNS_STATE_FILTER.ALL)
  const [expandedRowCampaignIds, setExpandedRowCampaignIds] = useState<readonly Key[]>([])
  const [selectedRowCampaignIds, setSelectedRowCampaignIds] = useState<Key[]>([])

  const [selectedTargetIds, setSelectedTargetIds] = useState<Key[]>([])
  const [campaignDataForTable, setCampaignDataForTable] = useState<ICampaignResult[] | null>(null)

  const { campaignData, loading } = useTypedSelector((select) => select.sponsoredBrands)
  const { dateRange, isOrder, defaultOrder } = useTypedSelector((select) => select.filter)
  const { placementData } = useTypedSelector((select) => select.sponsoredBrandsPlacements)
  const {
    fetchSponsoredBrandsTargets,
    fetchSponsoredBrandsPlacements,
    resetSponsoredBrandsPlacements,
    resetSponsoredBrandsTargets,
  } = useActions()

  useEffect(() => {
    resetSponsoredBrandsPlacements()
    resetSponsoredBrandsTargets()
    setIsExpandedAll(false)
    setExpandedRowCampaignIds([])
    setSelectedTargetIds([])
    setSelectedRowCampaignIds([])
  }, [dateRange.from, dateRange.to, isOrder, defaultOrder, stateFilter])

  useEffect(() => {
    const loadedCampaignIds = new Set(
      placementData.map((placement) => String(placement.campaignId))
    )
    const missingCampaignIds = expandedRowCampaignIds
      .map(String)
      .filter((campaignId) => !loadedCampaignIds.has(campaignId))

    if (missingCampaignIds.length === 0) {
      return
    }

    fetchSponsoredBrandsPlacements({
      fromDate: dateRange.from,
      toDate: dateRange.to,
      campaignIds: missingCampaignIds,
      isOrder,
      defaultOrder,
    })
  }, [expandedRowCampaignIds, placementData, dateRange.from, dateRange.to, isOrder, defaultOrder])

  function handleClickExpandAll() {
    if (isExpandedAll) {
      setIsExpandedAll(false)
      setExpandedRowCampaignIds([])
      return
    }

    const campaignIds = campaignData.campaignsResult.map((campaign) => {
      return campaign.campaignId
    })

    fetchSponsoredBrandsTargets({
      fromDate: dateRange.from,
      toDate: dateRange.to,
      campaignIds: campaignIds,
      filterType: isOrder ? FILTER_TYPES.ORDERS : FILTER_TYPES.DATE,
    })

    setExpandedRowCampaignIds(campaignIds)
    setIsExpandedAll(true)
  }

  function handleApplyCompleted() {
    setIsExpandedAll(false)
    setExpandedRowCampaignIds([])
    setSelectedTargetIds([])
    setSelectedRowCampaignIds([])
  }

  useEffect(() => {
    if (campaignData) {
      setCampaignDataForTable(campaignData.campaignsResult)
    }
  }, [campaignData])

  const kpi = useMemo(() => {
    const rows = campaignDataForTable ?? []
    let spend = 0
    let sales = 0
    let active = 0
    for (const c of rows) {
      spend += Number(c.spend) || 0
      sales += Number(c.sales) || 0
      if (c.campaignStatus === campaignStates.ENABLED) active += 1
    }
    return { spend, sales, active, total: rows.length }
  }, [campaignDataForTable])

  function getProjectGroups(isManaged: boolean) {
    return (
      <SponsoredBrandsProjectGroups
        isManaged={isManaged}
        stateFilter={stateFilter}
        isExpandedAll={isExpandedAll}
        expandedRowCampaignIds={expandedRowCampaignIds}
        setExpandedRowCampaignIds={setExpandedRowCampaignIds}
        selectedTargetIds={selectedTargetIds}
        setSelectedTargetIds={setSelectedTargetIds}
        campaignDataForTable={campaignDataForTable}
        setCampaignDataForTable={setCampaignDataForTable}
        setSelectedRowCampaignIds={setSelectedRowCampaignIds}
        selectedRowCampaignIds={selectedRowCampaignIds}
      />
    )
  }

  return (
    <Flex vertical={true} gap={10}>
      <KpiSummaryCards
        totalSpend={kpi.spend}
        totalSales={kpi.sales}
        activeCampaigns={kpi.active}
        totalCampaigns={kpi.total}
        loading={loading}
      />
      <Flex justify="space-between">
        <Space>
          <ExpandBtn
            onClick={handleClickExpandAll}
            text={isExpandedAll ? "Close All" : "Expand All"}
          />
          <SbStateFilter value={stateFilter} onChange={setStateFilter} />
        </Space>

        <Space>
          <CampaignActions
            selectedCampaignIds={selectedRowCampaignIds}
            setCampaignDataForTable={setCampaignDataForTable}
          />
          <ApplyOnAmazon
            selectedTargetIds={selectedTargetIds}
            stateFilter={stateFilter}
            onApplied={handleApplyCompleted}
          />
        </Space>
      </Flex>

      <ManageFilter
        ManagedCampaignsTable={getProjectGroups(true)}
        UnmanagedCampaignsTable={getProjectGroups(false)}
      />
    </Flex>
  )
}

export default SponsoredBrandsContainer
